import { getEarthAxis } from "../../simulation/geometry";
import type { Vector3 } from "../../simulation/types";
import {
  clamp,
  degreesToRadians,
  dot,
  normalize,
  radiansToDegrees,
  scale,
} from "../../simulation/vector";

export const YEAR_DAYS = 365;
export const JUNE_SOLSTICE_DAY = 172;
export const PERIHELION_DAY = 3;
export const APHELION_DAY = 185;
export const SEASON_ORBIT_RADIUS = 10.5;
/** Teaching exaggeration so the focus offset is visible. Real Earth e ≈ 0.0167. */
export const ORBIT_ECCENTRICITY = 0.22;
export const SEASON_SECONDS_AT_ONE_X = 48;

export const SEASON_NAMES = [
  "Northern summer / Southern winter",
  "September equinox",
  "Northern winter / Southern summer",
  "March equinox",
] as const;

export type SeasonName = (typeof SEASON_NAMES)[number];

export interface SeasonStation {
  readonly index: number;
  readonly dayOfYear: number;
  readonly shortName: string;
  readonly label: string;
}

// Station days sit on the quarter points of this uniform-angle teaching year
// so the equinox stations give exactly 0° declination and 12 h days.
export const SEASON_STATIONS: readonly SeasonStation[] = [
  {
    index: 0,
    dayOfYear: JUNE_SOLSTICE_DAY,
    shortName: "Jun",
    label: "June solstice",
  },
  {
    index: 1,
    dayOfYear: 263,
    shortName: "Sep",
    label: "September equinox",
  },
  {
    index: 2,
    dayOfYear: 355,
    shortName: "Dec",
    label: "December solstice",
  },
  {
    index: 3,
    dayOfYear: 81,
    shortName: "Mar",
    label: "March equinox",
  },
];

export interface LatitudeBand {
  readonly latitudeDegrees: number;
  readonly label: string;
}

export const SEASON_LATITUDES: readonly LatitudeBand[] = [
  { latitudeDegrees: 66.56, label: "Arctic Circle" },
  { latitudeDegrees: 40, label: "40°N" },
  { latitudeDegrees: 0, label: "Equator" },
  // Same Pretoria latitude as the horizon demo so the numbers match.
  { latitudeDegrees: -25.75, label: "25.8°S · Pretoria" },
];

export interface LatitudeReading extends LatitudeBand {
  readonly dayLengthHours: number;
  readonly noonAltitudeDegrees: number;
  readonly polarCondition: "midnight-sun" | "polar-night" | null;
}

export interface SeasonSnapshot {
  readonly dayOfYear: number;
  readonly yearAngleRadians: number;
  readonly earthPosition: Vector3;
  readonly sunDistance: number;
  readonly perihelionPosition: Vector3;
  readonly aphelionPosition: Vector3;
  readonly ellipseCenter: Vector3;
  readonly earthToSun: Vector3;
  readonly earthAxis: Vector3;
  readonly solarDeclinationDegrees: number;
  readonly seasonIndex: number;
  readonly seasonName: SeasonName;
  readonly station: SeasonStation;
  readonly latitudes: readonly LatitudeReading[];
}

export function wrapYearDay(day: number): number {
  return ((day % YEAR_DAYS) + YEAR_DAYS) % YEAR_DAYS;
}

export function yearAngleRadians(dayOfYear: number): number {
  return (
    ((wrapYearDay(dayOfYear) - JUNE_SOLSTICE_DAY) / YEAR_DAYS) * Math.PI * 2
  );
}

function circularDirection(dayOfYear: number): Vector3 {
  const angle = yearAngleRadians(dayOfYear);
  return normalize([-Math.sin(angle), 0, -Math.cos(angle)]);
}

export function perihelionDirection(): Vector3 {
  return circularDirection(PERIHELION_DAY);
}

export function getEllipseCenter(): Vector3 {
  return scale(
    perihelionDirection(),
    -SEASON_ORBIT_RADIUS * ORBIT_ECCENTRICITY,
  );
}

export function getEarthOrbitPosition(dayOfYear: number): Vector3 {
  const direction = circularDirection(dayOfYear);
  const cosineTrueAnomaly = dot(direction, perihelionDirection());
  const radius =
    (SEASON_ORBIT_RADIUS * (1 - ORBIT_ECCENTRICITY * ORBIT_ECCENTRICITY)) /
    (1 + ORBIT_ECCENTRICITY * cosineTrueAnomaly);
  return scale(direction, radius);
}

export function solarDeclinationDegrees(
  earthAxis: Vector3,
  earthToSun: Vector3,
): number {
  return radiansToDegrees(Math.asin(clamp(dot(earthAxis, earthToSun), -1, 1)));
}

export function noonAltitudeDegrees(
  latitudeDegrees: number,
  declinationDegrees: number,
): number {
  return 90 - Math.abs(latitudeDegrees - declinationDegrees);
}

export function dayLengthHours(
  latitudeDegrees: number,
  declinationDegrees: number,
): { hours: number; polarCondition: LatitudeReading["polarCondition"] } {
  const latitude = degreesToRadians(latitudeDegrees);
  const declination = degreesToRadians(declinationDegrees);
  const cosineHourAngle = -Math.tan(latitude) * Math.tan(declination);

  if (cosineHourAngle <= -1) {
    return { hours: 24, polarCondition: "midnight-sun" };
  }
  if (cosineHourAngle >= 1) {
    return { hours: 0, polarCondition: "polar-night" };
  }

  return {
    hours: (24 * Math.acos(cosineHourAngle)) / Math.PI,
    polarCondition: null,
  };
}

export function getSeasonSnapshot(dayOfYear: number): SeasonSnapshot {
  const day = wrapYearDay(dayOfYear);
  const angle = yearAngleRadians(day);
  const earthPosition = getEarthOrbitPosition(day);
  const earthToSun = normalize(scale(earthPosition, -1));
  const earthAxis = getEarthAxis();
  const declination = solarDeclinationDegrees(earthAxis, earthToSun);
  const wrappedAngle = ((angle % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
  const seasonIndex =
    Math.floor((wrappedAngle + Math.PI / 4) / (Math.PI / 2)) % 4;
  const seasonName = SEASON_NAMES[seasonIndex];
  const station = SEASON_STATIONS[seasonIndex];

  if (seasonName === undefined || station === undefined) {
    throw new RangeError(`Invalid season index: ${seasonIndex}`);
  }

  return {
    dayOfYear: day,
    yearAngleRadians: angle,
    earthPosition,
    earthToSun,
    earthAxis,
    solarDeclinationDegrees: declination,
    seasonIndex,
    seasonName,
    station,
    latitudes: SEASON_LATITUDES.map((band) => {
      const length = dayLengthHours(band.latitudeDegrees, declination);
      return {
        ...band,
        dayLengthHours: length.hours,
        noonAltitudeDegrees: noonAltitudeDegrees(
          band.latitudeDegrees,
          declination,
        ),
        polarCondition: length.polarCondition,
      };
    }),
    sunDistance: Math.hypot(
      earthPosition[0],
      earthPosition[1],
      earthPosition[2],
    ),
    perihelionPosition: getEarthOrbitPosition(PERIHELION_DAY),
    aphelionPosition: getEarthOrbitPosition(APHELION_DAY),
    ellipseCenter: getEllipseCenter(),
  };
}
